import React, { useState } from 'react';
import { RoteiroOperacionalAtivo } from '../../types/operacional';
import { 
  Truck, 
  MapPin, 
  Phone, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  Clock, 
  Navigation2, 
  AlertTriangle,
  Battery,
  Gauge,
  Info,
  Bell,
  Radio,
  Send
} from 'lucide-react';
import { rastreamentoNotificacaoService } from '../../services/rastreamentoNotificacaoService';

interface Props {
  roteiros: RoteiroOperacionalAtivo[];
  selectedRoteiroId?: number | null;
  onSelectRoteiro: (id: number) => void;
  onOpenOcorrenciaModal: (roteiro: RoteiroOperacionalAtivo) => void;
  onOpenNotificarModal?: (roteiro: RoteiroOperacionalAtivo) => void;
  onGpsSolicitadoFeedback?: (msg: string) => void;
}

export const ListaRoteirosAtivos: React.FC<Props> = ({
  roteiros,
  selectedRoteiroId,
  onSelectRoteiro,
  onOpenOcorrenciaModal,
  onOpenNotificarModal,
  onGpsSolicitadoFeedback
}) => {
  // Mantém os roteiros recolhidos por padrão para não poluir a tela
  const [expandedRoteiroIds, setExpandedRoteiroIds] = useState<number[]>([]);
  const [solicitandoGpsId, setSolicitandoGpsId] = useState<number | null>(null);
  const [gpsSolicitadoId, setGpsSolicitadoId] = useState<number | null>(null);

  const toggleExpand = (id: number) => {
    if (expandedRoteiroIds.includes(id)) {
      setExpandedRoteiroIds(expandedRoteiroIds.filter(item => item !== id));
    } else {
      setExpandedRoteiroIds([...expandedRoteiroIds, id]);
    }
  };

  const handleSolicitarGps = async (roteiro: RoteiroOperacionalAtivo) => {
    setSolicitandoGpsId(roteiro.id);
    try {
      const res = await rastreamentoNotificacaoService.solicitarLocalizacaoMotorista(
        roteiro.motorista_id,
        roteiro.motorista_nome
      );
      if (res.success) {
        setGpsSolicitadoId(roteiro.id);
        onGpsSolicitadoFeedback?.(`Solicitação de GPS enviada para ${roteiro.motorista_nome}! Aguardando sinal...`);
        setTimeout(() => {
          setGpsSolicitadoId(null);
        }, 4000);
      } else {
        alert('Erro ao solicitar GPS: ' + (res.error || 'Falha de comunicação'));
      }
    } finally {
      setSolicitandoGpsId(null);
    }
  };

  if (roteiros.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-8 text-center shadow-sm max-w-2xl mx-auto">
        <div className="w-14 h-14 bg-slate-100 text-slate-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Truck className="h-7 w-7" />
        </div>
        <h3 className="text-base font-bold text-slate-800">Nenhum Motorista em Viagem no Momento</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
          Os roteiros criados no sistema aparecerão nesta tela de monitoramento no instante em que os motoristas da frota fizerem login no App do Motorista e clicarem em <span className="font-semibold text-slate-700">"Iniciar Viagem"</span>.
        </p>
        <div className="mt-4 pt-4 border-t border-slate-100 inline-flex items-center gap-2 text-xs text-slate-400">
          <Info className="h-4 w-4 text-amber-500 shrink-0" />
          <span>Apenas viagens ativas e em trânsito da Frota Própria são exibidas no mapa.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
          <Truck className="h-5 w-5 text-red-600" />
          Roteiros em Andamento
        </h2>
        <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full border border-slate-200">
          {roteiros.length} em rota
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {roteiros.map((roteiro) => {
          const isExpanded = expandedRoteiroIds.includes(roteiro.id);
          const isSelected = selectedRoteiroId === roteiro.id;

          return (
            <div
              key={roteiro.id}
              className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden shadow-sm ${
                isSelected
                  ? 'border-red-500 ring-2 ring-red-500/10 shadow-md'
                  : 'border-slate-200/80 hover:border-slate-300'
              }`}
            >
              {/* Linha Principal Resumida */}
              <div className="p-3.5 flex flex-wrap items-center justify-between gap-3">
                {/* Motorista & Veículo */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                    {roteiro.motorista_nome.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-900 text-sm">{roteiro.motorista_nome}</h3>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                        {roteiro.veiculo_placa}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {roteiro.nome_roteiro} • <span className="font-medium text-slate-700">{roteiro.paradas_concluidas}/{roteiro.paradas_totais} paradas</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1 font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                        {roteiro.posicao_atual.ultima_atualizacao}
                      </span>
                      <span>•</span>
                      <span>{roteiro.posicao_atual.velocidade_kmh || 0} km/h</span>
                      <span>•</span>
                      <span>Bat: {roteiro.posicao_atual.bateria_pct || 100}%</span>
                    </div>
                  </div>
                </div>

                {/* Progresso e Ações */}
                <div className="flex items-center gap-2 ml-auto sm:ml-0">
                  {/* Botão Solicitar GPS em Tempo Real */}
                  <button
                    onClick={() => handleSolicitarGps(roteiro)}
                    disabled={solicitandoGpsId === roteiro.id}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition border ${
                      gpsSolicitadoId === roteiro.id
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700'
                    }`}
                    title="Solicitar atualização imediata de localização ao motorista"
                  >
                    <Radio className={`h-3.5 w-3.5 ${solicitandoGpsId === roteiro.id ? 'animate-spin text-blue-600' : 'text-blue-500'}`} />
                    <span>
                      {solicitandoGpsId === roteiro.id ? 'Pedindo...' : gpsSolicitadoId === roteiro.id ? 'Sinal Solicitado!' : 'Pedir GPS'}
                    </span>
                  </button>

                  {/* Botão Enviar Notificação / Mensagem */}
                  <button
                    onClick={() => onOpenNotificarModal?.(roteiro)}
                    className="p-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold flex items-center gap-1 transition"
                    title="Enviar notificação para o motorista"
                  >
                    <Bell className="h-4 w-4" />
                    <span className="hidden lg:inline text-[11px]">Notificar</span>
                  </button>

                  {/* Barra de Progresso Compacta */}
                  <div className="hidden md:flex items-center gap-2 min-w-[130px]">
                    <div className="w-20 bg-slate-100 h-2 rounded-full overflow-hidden border border-slate-200/60">
                      <div
                        className="bg-emerald-500 h-full rounded-full transition-all duration-300"
                        style={{ width: `${roteiro.percentual_concluido}%` }}
                      />
                    </div>
                    <span className="font-bold text-slate-700 text-xs min-w-[32px]">
                      {roteiro.percentual_concluido}%
                    </span>
                  </div>

                  <button
                    onClick={() => onSelectRoteiro(roteiro.id)}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition shadow-sm"
                  >
                    <Navigation2 className="h-3.5 w-3.5" />
                    <span>Mapa</span>
                  </button>

                  {roteiro.motorista_telefone && (
                    <a
                      href={`tel:${roteiro.motorista_telefone}`}
                      className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition"
                      title="Ligar para Motorista"
                    >
                      <Phone className="h-4 w-4" />
                    </a>
                  )}

                  <button
                    onClick={() => onOpenOcorrenciaModal(roteiro)}
                    className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-xs font-medium transition"
                    title="Registrar Ocorrência"
                  >
                    <AlertTriangle className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => toggleExpand(roteiro.id)}
                    className="p-1.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition"
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Detalhes Expandidos (Apenas se o usuário abrir) */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50/50 space-y-3">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Sequência de Paradas
                  </div>

                  <div className="space-y-2">
                    {roteiro.paradas.map((parada) => {
                      const isConcluida = parada.status_parada === 'concluida';
                      const isEmDeslocamento = parada.status_parada === 'em_deslocamento' || parada.status_parada === 'chegou';
                      const isColeta = parada.tipo_parada === 'coleta';

                      return (
                        <div
                          key={parada.id}
                          className={`p-2.5 rounded-lg border text-xs flex items-center justify-between gap-3 ${
                            isEmDeslocamento
                              ? 'bg-blue-50/70 border-blue-200'
                              : isConcluida
                              ? 'bg-white border-slate-200 opacity-70'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${
                              isConcluida ? 'bg-slate-300 text-slate-700' : isColeta ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'
                            }`}>
                              {parada.ordem_sequencia}
                            </span>

                            <div className="min-w-0">
                              <p className="font-semibold text-slate-800 truncate">{parada.cliente_nome}</p>
                              <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
                                {parada.endereco}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                              isColeta ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {isColeta ? 'Coleta' : 'Entrega'}
                            </span>

                            {isConcluida ? (
                              <span className="text-emerald-600 font-semibold text-[11px] flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Concluído
                              </span>
                            ) : (
                              <span className="text-slate-500 text-[11px]">
                                {parada.horario_janela_inicio ? `${parada.horario_janela_inicio} - ${parada.horario_janela_fim}` : 'Pendente'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
